"use client";

import React, { useEffect, useState } from "react";
import MediaQrCode from "@/app/components/MediaQrCode";
import {
  GALLERY_QR_OVERLAY_MS,
  GALLERY_QR_PREVIEW_MS,
} from "@/app/lib/galleryQrTiming";

export default function GalleryQrOverlay({
  path,
  mediaType,
  suspendCycle = false,
}: {
  path: string;
  mediaType: "photo" | "video";
  suspendCycle?: boolean;
}) {
  const [showQr, setShowQr] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [cycleKey, setCycleKey] = useState(0);
  const mediaLabel = mediaType === "photo" ? "photo" : "video";
  const isQrVisible = showQr && !suspendCycle;

  useEffect(() => {
    if (manualOpen || suspendCycle) return;

    let timer: number | undefined;
    let cancelled = false;

    function startPreviewTimer() {
      timer = window.setTimeout(() => {
        if (cancelled) return;
        setShowQr(true);

        timer = window.setTimeout(() => {
          if (cancelled) return;
          setShowQr(false);
          startPreviewTimer();
        }, GALLERY_QR_OVERLAY_MS);
      }, GALLERY_QR_PREVIEW_MS);
    }

    startPreviewTimer();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [cycleKey, manualOpen, path, suspendCycle]);

  function openQr() {
    setManualOpen(true);
    setShowQr(true);
  }

  function closeQr() {
    setManualOpen(false);
    setShowQr(false);
    setCycleKey((value) => value + 1);
  }

  return (
    <>
      {!isQrVisible && !suspendCycle ? (
        <button
          type="button"
          onClick={openQr}
          className="absolute right-3 top-3 z-20 rounded-full border border-white/60 bg-black/55 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white shadow-lg backdrop-blur transition hover:bg-black/70"
          aria-label={`Show QR code for this ${mediaLabel}`}
        >
          Show QR
        </button>
      ) : null}

      {isQrVisible ? (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/62 px-4 py-5 text-center text-white backdrop-blur-[2px]">
          <div className="w-full max-w-[15rem] rounded-[1.5rem] border border-[#ead8a0] bg-[#fffaf0] p-4 text-[#1f3f6d] shadow-2xl">
            <a
              href={path}
              className="mx-auto block w-fit rounded-[1rem] bg-[#fffaf0] p-2"
              aria-label={`Open download page for this ${mediaLabel}`}
            >
              <MediaQrCode path={path} size={172} />
            </a>
            <p className="mt-3 text-sm font-semibold leading-5">
              Scan to download this {mediaLabel}
            </p>
            <button
              type="button"
              onClick={closeQr}
              className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
              aria-label={`Back to ${mediaLabel} preview`}
            >
              Back to preview
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
